import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Mail, MapPin, ArrowUpRight } from 'lucide-react';

// Premium Universal Footer Component
// Used across all pages for consistent branding and navigation

export default function PremiumFooter({ variant = 'dark' }) {
  const navigate = useNavigate();
  
  const isDark = variant === 'dark';
  const bgClass = isDark ? 'bg-slate-950' : 'bg-stone-100';
  const borderClass = isDark ? 'border-slate-800' : 'border-stone-300';
  const textPrimary = isDark ? 'text-white' : 'text-stone-900';
  const textSecondary = isDark ? 'text-slate-400' : 'text-stone-600';
  const textMuted = isDark ? 'text-slate-600' : 'text-stone-400';
  const linkHover = isDark ? 'hover:text-amber-400' : 'hover:text-amber-700';
  const accentColor = 'text-amber-500';

  const mainLinks = [
    { label: 'Belonging eBook', href: '/belonging-ebook', description: 'Interactive NVC Workbook' },
    { label: 'Wisdom Library', href: '/wisdom', description: 'Stories of Belonging' },
    { label: 'LORE Archive', href: '/lore', description: 'Learning & Reflections' },
    { label: 'OFNR Trainer', href: '/ofnr-trainer', description: 'Interactive Practice Tools' },
  ];

  const toolLinks = [
    { label: 'OFNR Trainer', href: '/ofnr-trainer' },
    { label: 'Conflict Navigator', href: '/conflict-navigator' },
    { label: 'Emergent Empathy', href: '/emergent-empathy' },
    { label: 'Family Constellation', href: '/constellation' },
  ];

  const ecosystemLinks = [
    { label: 'Belonging Sessions', href: '/', internal: true },
    { label: 'Defiance Springs', href: 'https://defiancesprings.com', external: true },
    { label: 'Planetary Fam', href: 'https://planetaryfam.com', external: true },
  ];

  return (
    <footer className={`${bgClass} border-t ${borderClass}`}>
      {/* Main Footer Content */}
      <div className="max-w-7xl mx-auto px-6 py-16">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-12">
          
          {/* Brand Column */}
          <div className="lg:col-span-1">
            <h3 className={`text-2xl font-light tracking-wide ${textPrimary} mb-4`} style={{ fontFamily: 'Playfair Display, serif' }}>
              Belonging Sessions
            </h3>
            <p className={`text-sm ${textSecondary} leading-relaxed mb-6`}>
              The practice of Nonviolent Communication for families and communities seeking deeper connection.
            </p>
            <div className={`text-xs ${textMuted} space-y-2`}>
              <p className="flex items-center gap-2">
                <MapPin className="w-3 h-3" />
                Defiance Springs - Viroqua, Wisconsin USA
              </p>
              <a 
                href="mailto:hello@defiancesprings.com" 
                className={`flex items-center gap-2 ${linkHover} transition-colors`}
              >
                <Mail className="w-3 h-3" />
                hello@defiancesprings.com
              </a>
            </div>
          </div>

          {/* Experience Column */}
          <div>
            <h4 className={`text-xs font-semibold tracking-[0.2em] uppercase ${textMuted} mb-6`}>
              Experience
            </h4>
            <ul className="space-y-4">
              {mainLinks.map(link => (
                <li key={link.href}>
                  <button
                    onClick={() => navigate(link.href)}
                    className={`group text-left`}
                  >
                    <span className={`block text-sm font-medium ${textPrimary} ${linkHover} transition-colors`}>
                      {link.label}
                    </span>
                    <span className={`block text-xs ${textMuted}`}>
                      {link.description}
                    </span>
                  </button>
                </li>
              ))}
            </ul>
          </div>

          {/* Ecosystem Column */}
          <div>
            <h4 className={`text-xs font-semibold tracking-[0.2em] uppercase ${textMuted} mb-6`}>
              Ecosystem
            </h4>
            <ul className="space-y-3">
              {ecosystemLinks.map(link => (
                <li key={link.href}>
                  {link.external ? (
                    <a
                      href={link.href}
                      target="_blank"
                      rel="noopener noreferrer"
                      className={`text-sm ${textSecondary} ${linkHover} transition-colors inline-flex items-center gap-1`}
                    >
                      {link.label}
                      <ArrowUpRight className="w-3 h-3" />
                    </a>
                  ) : (
                    <button
                      onClick={() => navigate(link.href)}
                      className={`text-sm ${textSecondary} ${linkHover} transition-colors`}
                    >
                      {link.label}
                    </button>
                  )}
                </li>
              ))}
            </ul>

            {/* ElchmE Mini Brand */}
            <div className={`mt-8 pt-6 border-t ${borderClass}`}>
              <button
                onClick={() => navigate('/elchme')}
                className="group"
              >
                <span className={`text-lg font-light tracking-wide`}>
                  <span className="text-blue-400">El</span>
                  <span className="text-red-400">ch</span>
                  <span className="text-emerald-400">m</span>
                  <span className="text-amber-400">E</span>
                </span>
                <span className={`block text-[10px] ${textMuted} group-hover:${accentColor} transition-colors`}>
                  Empathetic Alchemy
                </span>
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Bottom Bar */}
      <div className={`border-t ${borderClass}`}>
        <div className="max-w-7xl mx-auto px-6 py-6">
          <div className="flex flex-col md:flex-row justify-between items-center gap-4">
            <p className={`text-xs ${textMuted}`}>
              © {new Date().getFullYear()} Belonging Sessions. A practice in the Language of Life.
            </p>
            <div className={`flex items-center gap-6 text-xs ${textMuted}`}>
              <button onClick={() => navigate('/login')} className={`${linkHover} transition-colors`}>
                Member Login
              </button>
              <span>·</span>
              <button onClick={() => navigate('/admin')} className={`${linkHover} transition-colors`}>
                Admin
              </button>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
}
